// ═══════════════════════════════════════════════════════════════════════════════
// Homepage-style auth entry buttons (email / Google / register)
// ═══════════════════════════════════════════════════════════════════════════════

import { useLanguage } from '../../contexts/LanguageContext';
import { hasGoogleClientId } from '../../lib/googleIdentity';

const EmailLoginIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden>
    <path
      d="M4 6h16a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Zm0 2v.2l8 5.2 8-5.2V8H4Zm16 8V10.6l-7.5 4.9a1 1 0 0 1-1.1 0L4 10.6V16h16Z"
      fill="currentColor"
    />
  </svg>
);

const GoogleLoginIcon = () => (
  <svg viewBox="0 0 18 18" className="h-5 w-5" aria-hidden>
    <path
      fill="#EA4335"
      d="M9 3.48c1.69 0 2.83.73 3.48 1.34l2.54-2.54C13.49.85 11.46 0 9 0 5.48 0 2.44 2.02.96 4.96l2.95 2.29C4.63 5.07 6.64 3.48 9 3.48Z"
    />
    <path
      fill="#4285F4"
      d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72l2.78 2.15c1.62-1.49 2.56-3.69 2.56-6.31Z"
    />
    <path
      fill="#FBBC05"
      d="M3.91 10.75a5.42 5.42 0 0 1 0-3.5L.96 4.96a9 9 0 0 0 0 8.08l2.95-2.29Z"
    />
    <path
      fill="#34A853"
      d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.78-2.15c-.77.52-1.75.83-3.18.83-2.36 0-4.37-1.6-5.09-3.75L.96 13.04C2.44 15.98 5.48 18 9 18Z"
    />
  </svg>
);

type AuthLandingNavButtonsProps = {
  onEmailLogin: () => void;
  onGoogleLogin: () => void;
  onRegister: () => void;
  googleBusy?: boolean;
  className?: string;
};

export const AuthLandingNavButtons = ({
  onEmailLogin,
  onGoogleLogin,
  onRegister,
  googleBusy = false,
  className = 'w-full space-y-4',
}: AuthLandingNavButtonsProps) => {
  const { t } = useLanguage();

  return (
    <nav className={className} aria-label={t('common.appTagline')}>
      <button
        type="button"
        onClick={onEmailLogin}
        className="inline-flex h-14 w-full items-center justify-center gap-2 rounded-full bg-primary-700 px-5 text-lg font-semibold tracking-[0.01em] text-white shadow-sm transition hover:bg-primary-800"
      >
        <EmailLoginIcon />
        {t('home.navLoginEmail')}
      </button>
      <button
        type="button"
        disabled={!hasGoogleClientId() || googleBusy}
        onClick={onGoogleLogin}
        className="inline-flex h-14 w-full items-center justify-center gap-2 rounded-full border border-gray-200 bg-white px-5 text-lg font-semibold tracking-[0.01em] text-gray-800 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800"
      >
        <GoogleLoginIcon />
        {t('home.navGoogleLogin')}
      </button>
      <button
        type="button"
        onClick={onRegister}
        className="inline-flex h-14 w-full items-center justify-center rounded-full border border-[#0F5A42] bg-[#E6F4ED] px-5 text-lg font-semibold tracking-[0.01em] text-[#0F5A42] shadow-sm transition hover:bg-[#DCEFE5] dark:border-[#4FBF8F] dark:bg-[#123326] dark:text-[#9AE6BF] dark:hover:bg-[#184532]"
      >
        {t('home.navRegister')}
      </button>
    </nav>
  );
};
